import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  contentChildren,
  effect,
  ElementRef,
  inject,
  input,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { UiBottomSheetComponent } from '../bottom-sheet/ui-bottom-sheet.component';
import { UiPopoverComponent } from '../popover/ui-popover.component';
import { UiMenuItemCustomComponent } from './ui-menu-item-custom.component';
import { UiMenuItemComponent } from './ui-menu-item.component';
import { UI_MENU_CONTEXT, UiMenuContext } from './ui-menu-context.token';

@Component({
  selector: 'ui-menu',
  imports: [NgTemplateOutlet, UiBottomSheetComponent, UiPopoverComponent],
  standalone: true,
  templateUrl: './ui-menu.component.html',
  providers: [
    {
      provide: UI_MENU_CONTEXT,
      useFactory: (): UiMenuContext => {
        const menu = inject(UiMenuComponent);
        return { close: () => menu.hide() };
      },
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UiMenuComponent {
  readonly mobile = input(false); // TODO da capire se non vada standardizzato per tutti i menu
  readonly headerTemplate = input<TemplateRef<unknown> | null>(null);
  readonly closeFocus = input<(() => void) | null>(null);

  private readonly popover = viewChild(UiPopoverComponent);
  private readonly sheet = viewChild(UiBottomSheetComponent);

  private readonly menuItems = contentChildren(UiMenuItemComponent, {
    read: ElementRef<HTMLElement>,
  });
  private readonly customItems = contentChildren(UiMenuItemCustomComponent, {
    read: ElementRef<HTMLElement>,
  });

  /** All menu rows in DOM order (both item types), as host elements.
   * Sorted fresh on every use, not cached: while the popover is closed its
   * overlay is removed from the DOM (`@if (render)`), and the content query
   * can reorder during that teardown/re-insert cycle — leaving a stale
   * (reversed) order cached. The live DOM is only reliable while the menu
   * is open, so we always re-sort at use time. */
  private items(): HTMLElement[] {
    const elements = [...this.menuItems(), ...this.customItems()].map(
      (ref) => ref.nativeElement,
    );
    return elements.sort((a, b) =>
      a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1,
    );
  }

  /** ARIA roving tabindex: only the focused row is tabbable. */
  private readonly rovingTabindex = effect(() => {
    this.menuItems();
    this.customItems();
    this.syncRovingTabindex();
  });

  show(target: HTMLElement): void {
    if (this.mobile()) {
      this.sheet()?.show();
    } else {
      this.popover()?.show(target);
      // Wait for the popover overlay to be appended, then land focus on the
      // first row so arrow keys work immediately.
      setTimeout(() => {
        this.items()[0]?.focus();
        this.syncRovingTabindex();
      }, 50);
    }
  }

  hide(): void {
    if (this.mobile()) {
      this.sheet()?.hide();
    } else {
      this.popover()?.hide();
    }
  }

  /** Roving focus across the projected rows — ArrowUp/Down, Home/End, Escape. */
  onListKeydown(event: KeyboardEvent): void {
    const items = this.items();
    const index = items.indexOf(event.target as HTMLElement);

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.focusItem(items, index < 0 ? 0 : index + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.focusItem(items, index < 0 ? 0 : index - 1);
        break;
      case 'Home':
        event.preventDefault();
        this.focusItem(items, 0);
        break;
      case 'End':
        event.preventDefault();
        this.focusItem(items, items.length - 1);
        break;
      case 'Escape':
        event.preventDefault();
        this.hide();
        this.closeFocus()?.();
        break;
    }
  }

  private focusItem(items: HTMLElement[], index: number): void {
    if (items.length === 0) {
      return;
    }
    items[Math.max(0, Math.min(index, items.length - 1))]?.focus();
    this.syncRovingTabindex();
  }

  private syncRovingTabindex(): void {
    const active = document.activeElement;
    for (const el of this.items()) {
      el.tabIndex = el === active ? 0 : -1;
    }
  }
}
